/**
 * Questions fréquentes servies par le bot WhatsApp.
 *
 * L'accueil les met à jour ici, sans développeur. Seules les questions
 * actives sont proposées aux patients ; le bot les reconnaît aussi en texte
 * libre grâce aux mots-clés.
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi } from '../../services/appointmentsApi';
import type { FaqEntry } from '../../types/appointments';

const VIDE: Partial<FaqEntry> = { question: '', reponse: '', mots_cles: '', envoyer_position: false, lister_assurances: false, ordre: 0, actif: true };

const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none';

const FaqManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [edition, setEdition] = useState<Partial<FaqEntry> | null>(null);
  const [erreur, setErreur] = useState('');

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ['faq-whatsapp'],
    queryFn: appointmentsApi.getFaq,
  });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['faq-whatsapp'] });

  const enregistrer = useMutation({
    mutationFn: appointmentsApi.saveFaq,
    onSuccess: () => { setEdition(null); setErreur(''); rafraichir(); },
    onError: () => setErreur("Enregistrement impossible : vérifiez la question et la réponse."),
  });

  const supprimer = useMutation({
    mutationFn: appointmentsApi.deleteFaq,
    onSuccess: rafraichir,
  });

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-800">Questions fréquentes du bot</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Seules les questions actives sont proposées aux patients. Sans réponse adaptée, le bot propose l'accueil.
          </p>
        </div>
        <button onClick={() => setEdition({ ...VIDE, ordre: questions.length })}
          className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
          + Ajouter
        </button>
      </div>

      {edition && (
        <form
          onSubmit={e => { e.preventDefault(); enregistrer.mutate(edition); }}
          className="border border-sky-100 bg-sky-50/40 rounded-xl p-4 space-y-3"
        >
          <div>
            <label htmlFor="faq-question" className="text-xs text-gray-600 mb-1 block">Question *</label>
            <input id="faq-question" required maxLength={200} value={edition.question ?? ''}
              onChange={e => setEdition(f => ({ ...f, question: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label htmlFor="faq-reponse" className="text-xs text-gray-600 mb-1 block">Réponse *</label>
            <textarea id="faq-reponse" required rows={4} value={edition.reponse ?? ''}
              onChange={e => setEdition(f => ({ ...f, reponse: e.target.value }))} className={`${inputClass} resize-none`} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label htmlFor="faq-mots" className="text-xs text-gray-600 mb-1 block">Mots-clés (séparés par des virgules)</label>
              <input id="faq-mots" maxLength={300} value={edition.mots_cles ?? ''} placeholder="horaires, ouvert, fermé"
                onChange={e => setEdition(f => ({ ...f, mots_cles: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label htmlFor="faq-ordre" className="text-xs text-gray-600 mb-1 block">Ordre</label>
              <input id="faq-ordre" type="number" min={0} value={edition.ordre ?? 0}
                onChange={e => setEdition(f => ({ ...f, ordre: Number(e.target.value) || 0 }))} className={inputClass} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={edition.actif ?? true}
                onChange={e => setEdition(f => ({ ...f, actif: e.target.checked }))} className="rounded text-sky-600" />
              <span className="text-sm text-gray-700">Active (proposée aux patients)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={edition.envoyer_position ?? false}
                onChange={e => setEdition(f => ({ ...f, envoyer_position: e.target.checked }))} className="rounded text-sky-600" />
              <span className="text-sm text-gray-700">Joindre la position du cabinet</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={edition.lister_assurances ?? false}
                onChange={e => setEdition(f => ({ ...f, lister_assurances: e.target.checked }))} className="rounded text-sky-600" />
              <span className="text-sm text-gray-700">
                Insérer la liste des assurances acceptées (à la place de <code>{'{assurances}'}</code>, sinon à la fin)
              </span>
            </label>
          </div>
          {erreur && <p className="text-sm text-red-600">{erreur}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setEdition(null); setErreur(''); }}
              className="border border-gray-200 text-gray-600 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={enregistrer.isLoading}
              className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {enregistrer.isLoading ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : questions.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune question pour l'instant.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {questions.map(q => (
            <li key={q.id} className="py-3 flex gap-3 items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">{q.question}</span>
                  {!q.actif && <span className="badge-yellow">Inactive</span>}
                  {q.envoyer_position && <span className="badge-blue">+ position</span>}
                  {q.lister_assurances && <span className="badge-blue">+ assurances</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{q.reponse}</p>
                {q.mots_cles && <p className="text-xs text-gray-400 mt-0.5">Mots-clés : {q.mots_cles}</p>}
              </div>
              <button onClick={() => setEdition(q)} aria-label={`Modifier « ${q.question} »`}
                className="son-icon-btn is-blue text-xs px-2">Modifier</button>
              <button
                onClick={() => window.confirm(`Supprimer « ${q.question} » ?`) && supprimer.mutate(q.id)}
                aria-label={`Supprimer « ${q.question} »`}
                className="son-icon-btn is-red text-xs px-2">Supprimer</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FaqManager;
